import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterRenderEffect,
  inject,
  input,
  output,
} from '@angular/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
  type MatDatepickerInputEvent,
} from '@angular/material/datepicker';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { TranslateModule } from '@ngx-translate/core';
import { FieldKind, toIsoDate, type DocField } from '../../_lib/docx-core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { DateFormat } from './date-format.directive';
import { DocStore } from './doc-store';
import { UNKNOWN_DATE, dateFromIso, isoFromDate } from './editor-date-adapter';

export interface FocusedField {
  readonly id: string;
}

@Component({
  selector: 'app-field-panel',
  imports: [
    TranslateModule,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatIconButton,
    MatIcon,
    DateFormat,
    CdkTextareaAutosize,
  ],
  templateUrl: './field-panel.html',
  styleUrl: './field-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldPanel {
  public readonly fields = input.required<readonly DocField[]>();
  public readonly focused = input<FocusedField | null>(null);
  public readonly changed = output<void>();
  public readonly typed = output<string>();

  protected readonly kind = FieldKind;

  private readonly store = inject(DocStore);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor() {
    afterRenderEffect(() => {
      const focused = this.focused();
      if (!focused) {
        return;
      }

      const fields = this.host.nativeElement.querySelectorAll<HTMLElement>('.field');
      const field = Array.from(fields).find((f) => f.dataset['fieldId'] === focused.id);
      field?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }

  protected setText(field: DocField, event: Event): void {
    this.apply(field, (event.target as HTMLInputElement | HTMLTextAreaElement).value);
  }

  protected typeText(field: DocField, event: Event): void {
    this.store.setValue(field.id, (event.target as HTMLInputElement | HTMLTextAreaElement).value);
    this.typed.emit(field.id);
  }

  protected setChecked(field: DocField, event: Event): void {
    this.apply(field, (event.target as HTMLInputElement).checked);
  }

  protected asText(field: DocField): string {
    return typeof field.value === 'string' ? field.value : '';
  }

  protected dateOf(field: DocField): Date | null {
    return dateFromIso(field.value);
  }

  // The text of a date field that holds no date, such as "Onbekend".
  protected dateText(field: DocField): string {
    return typeof field.value === 'string' && !toIsoDate(field.value) ? field.value : '';
  }

  // A picked or correctly typed date is stored; an emptied input makes the date
  // unknown again; text that is not a date is ignored, so a typo does not wipe it.
  protected setDate(field: DocField, event: MatDatepickerInputEvent<Date>): void {
    if (event.value) {
      this.apply(field, isoFromDate(event.value));

      return;
    }

    if (event.targetElement instanceof HTMLInputElement && event.targetElement.value.trim() === '') {
      this.clearDate(field);
    }
  }

  protected clearDate(field: DocField): void {
    this.apply(field, UNKNOWN_DATE);
  }

  protected asChecked(field: DocField): boolean {
    return field.value === true;
  }

  private apply(field: DocField, value: string | boolean): void {
    this.store.setValue(field.id, value);
    this.changed.emit();
  }
}
